import { useTranslation } from 'react-i18next';
import { type Document } from '@/api/queries/storage';

interface StorageTableProps {
  documents: Document[];
  formatFileSize: (bytes: number) => string;
  isLoading: boolean;
  selectedDocuments: string[];
  onDocumentSelect: (documentId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onFileClick: (document: Document) => void;
}

export function StorageTable({
  documents,
  isLoading,
  formatFileSize,
  selectedDocuments,
  onDocumentSelect,
  onSelectAll,
  onFileClick,
}: StorageTableProps) {
  const { t: tStorage } = useTranslation('storage');
  const { t: tCommon } = useTranslation();

  const formatDate = (date: string) => {
    return tCommon('format.date', {
      date: new Date(date),
      formatParams: {
        date: {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        },
      },
    });
  };

  const allSelected = documents.length > 0 && selectedDocuments.length === documents.length;
  const someSelected = selectedDocuments.length > 0 && selectedDocuments.length < documents.length;

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p>{tStorage('loadingDocuments')}</p>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50">
        <p className="text-gray-600">{tStorage('noDocumentsFound')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-6 py-3 text-left">
              <div className="flex items-center gap-3">
                <label
                  className="inline-flex items-center p-2 -m-2 cursor-pointer"
                  onClick={e => {
                    e.stopPropagation(); // Prevent to trigger the row click (file details navigation)
                  }}
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={allSelected}
                    ref={input => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={e => onSelectAll(e.target.checked)}
                    data-testid="select-all-checkbox"
                  />
                </label>
                <span className="text-xs font-semibold text-gray-600 tracking-wider">
                  {tStorage('fileName')}
                </span>
                <svg
                  className="w-3 h-3 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </th>
            <th className="px-6 py-3 text-left">
              <span className="text-xs font-semibold text-gray-600 tracking-wider">
                {tStorage('fileSizeTitle')}
              </span>
            </th>
            <th className="px-6 py-3 text-left">
              <span className="text-xs font-semibold text-gray-600 tracking-wider">
                {tStorage('dateUploaded')}
              </span>
            </th>
            <th className="px-6 py-3 text-left">
              <span className="text-xs font-semibold text-gray-600 tracking-wider">
                {tStorage('lastUpdated')}
              </span>
            </th>
            <th className="px-6 py-3 text-left">
              <span className="text-xs font-semibold text-gray-600 tracking-wider">
                {tStorage('uploadedByHeader')}
              </span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {documents.map((document: Document) => (
            <tr
              key={document.id}
              className="hover:bg-gray-50 cursor-pointer focus-within:bg-gray-50"
              onClick={() => onFileClick(document)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onFileClick(document);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={tStorage('viewDetailsFor', { fileName: document.name })}
            >
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <label
                    className="inline-flex items-center p-2 -m-2 cursor-pointer"
                    onClick={e => {
                      e.stopPropagation(); // Prevent row click when clicking checkbox
                    }}
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={selectedDocuments.includes(document.id)}
                      onChange={e => {
                        onDocumentSelect(document.id, e.target.checked);
                      }}
                      onClick={e => {
                        e.stopPropagation(); // Prevent row click when clicking checkbox
                      }}
                      data-testid={`document-checkbox-${document.id}`}
                    />
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-10 min-w-10 h-10 bg-gray-200 rounded-sm flex items-center justify-center">
                      <span className="text-xs text-gray-600 font-medium">
                        {/* TODO: Add thumbnail for file type: https://sitetraker.atlassian.net/browse/SIT-52396 */}
                        {document.file_type}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{document.name}</div>
                      <div className="text-sm text-gray-500">{formatFileSize(document.size)}</div>
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-500">{formatFileSize(document.size)}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-500">{formatDate(document.created_date)}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-500">
                  {formatDate(document.last_modified_date)}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-500">{document.created_by}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
